import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Permission} from 'smag-commons/dto/api';
import {PermissionNamePipe} from '../permission.pipe';
import * as _ from 'lodash';
import {PermissionPattern} from '../../../shared/permissions';

export interface PermissionSettings {
    mode: PermissionMode;
}

export enum PermissionMode {
    SINGLE,
    PATTERN
}

export interface SinglePermissionSettings extends PermissionSettings {
    mode: PermissionMode.SINGLE;
    allPermissions: Permission[];
}

export interface PatternPermissionSettings extends PermissionSettings {
    mode: PermissionMode.PATTERN;
    allPatterns: PermissionPattern[];
}

@Component({
    selector: 'app-permission-list',
    templateUrl: './permission-list.component.html',
    styleUrls: ['./permission-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PermissionListComponent implements OnInit, OnChanges {

    private pnp: PermissionNamePipe;

    @Input() readOnly: boolean;
    @Input() settings: PermissionSettings;
    @Input() permissions: Permission[];
    @Output() permissionsChange: EventEmitter<Permission[]> = new EventEmitter<Permission[]>();

    availablePermissions: Permission[]; // used only in single mode
    availablePatterns: PermissionPattern[]; // used only in pattern mode
    currentPermissions: Permission[];

    constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
        this.pnp = new PermissionNamePipe();
        this.availablePermissions = null;
        this.currentPermissions = null;
    }

    ngOnInit() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('settings' in changes || 'permissions' in changes) {
            this.update();
        }
    }

    get singleMode(): boolean {
        return this.settings?.mode === PermissionMode.SINGLE;
    }

    get patternMode(): boolean {
        return this.settings?.mode === PermissionMode.PATTERN;
    }

    onClickAddPermissionMenu(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
    }

    onClickAddPermission(permission: Permission) {
        const newPermissions = permission === Permission.ALL ? [Permission.ALL] : [...this.permissions, permission];
        this.permissionsChange.next(newPermissions);
        // NOTE: we do not change this.permissions on purpose, so that update of [permission] is effectively left to
        //       the owner of app-permission-list (this way we can easily avoid changing permissions even upon REST
        //       call failure)
    }

    onClickRemovePermission(permission: Permission) {
        const newPermissions = this.permissions.filter(p => p !== permission);
        this.permissionsChange.next(newPermissions);
        // NOTE: we do not change this.permissions on purpose, so that update of [permission] is effectively left to
        //       the owner of app-permission-list (this way we can easily avoid changing permissions even upon REST
        //       call failure)
    }

    onClickSetPattern(pattern: PermissionPattern) {
        this.permissionsChange.next(pattern.permissions);
        // NOTE: we do not change this.permissions on purpose, so that update of [permission] is effectively left to
        //       the owner of app-permission-list (this way we can easily avoid changing permissions even upon REST
        //       call failure)
    }

    private update() {
        if (this.permissions.includes(Permission.ALL) && this.permissions.length > 1) { // sanity check
            this.permissions = [Permission.ALL];
        }
        switch (this.settings?.mode) {
            case PermissionMode.SINGLE: {
                const allPermissions = (this.settings as SinglePermissionSettings).allPermissions;
                const availablePermissions = this.permissions.includes(Permission.ALL) ? [] : allPermissions.filter(p => !this.permissions.includes(p));
                this.availablePermissions = this.sortPermissions(availablePermissions);
                break;
            }
            case PermissionMode.PATTERN: {
                this.availablePatterns = (this.settings as PatternPermissionSettings).allPatterns;
                break;
            }
        }
        this.currentPermissions = this.sortPermissions(this.permissions);
        this.changeDetectorRef.markForCheck();
    }

    private sortPermissions(permissions: Permission[]): Permission[] {
        const SPECIAL_PERMS_1 = [
            Permission.CREATE,
            Permission.READ,
            Permission.UPDATE,
            Permission.DELETE,
        ];
        const SPECIAL_PERMS_2 = [
            Permission.PERMISSIONS,
            Permission.ALL
        ];
        return [
            ...(SPECIAL_PERMS_1.filter(p => permissions.includes(p))),
            ...(_.sortBy(permissions.filter(p => !SPECIAL_PERMS_1.includes(p) && !SPECIAL_PERMS_2.includes(p)),
                p => this.pnp.transform(p))),
            ...(SPECIAL_PERMS_2.filter(p => permissions.includes(p))),
        ];
    }
}
